import type React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import backgroundImage from "../../assets/pdf/pdf_background_image.png";
// import logo from "../../assets/pdf/Pasted image.png";
const PDF_CUSTOM_UPLOAD_BASE_URL = import.meta.env.VITE_PDF_CUSTOM_UPLOAD_BASE_URL;
const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;
const logo = VISUALIZE__URL + "Pasted image.webp";
// Create styles
const styles = StyleSheet.create({
  page: {
    width: "210mm",
    height: "297mm",
    position: "relative",
    backgroundColor: "white", // Fallback if background image fails
  },
  backgroundImage: {
    position: "absolute",
    width: "210mm",
    height: "297mm",
  },
  header: {
    padding: "5mm",
    backgroundColor: "white",
    borderBottom: "1pt solid #1976d2",
    marginBottom: "3mm",
  },
  headerText: {
    fontSize: 16,
    color: "#064d94",
    textAlign: "center",
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },
  content: {
    flexGrow: 1, // Fills the available space
    padding: "5mm",
    display: "flex",
    flexDirection: "column",
    gap: "2mm",
    minHeight: "247mm",
  },
  section: {
    padding: "1mm",
    width: "100%",
  },
  sectionTitle: {
    fontSize: 14,
    color: "#064d94",
    marginBottom: "1.5mm",
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },
  text: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#3b3939",
  },
  image: {
    width: "100%",
    height: "56mm",
    objectFit: "contain",
    marginVertical: "2.5mm",
  },
  footer: {
    backgroundColor: "#064d94",
    padding: "5mm",
    height: "20mm",
  },
  footerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  footerText: {
    color: "white",
    fontSize: 10,
    alignItems: "flex-end",
  },
  fullHeightImage: {
    flex: 1,
    height: "100%",
    objectFit: "cover",
  },
  imageContainer: {
    flexDirection: "column",
    gap: "2mm",
    marginVertical: "2mm",
    width: "auto",
    height: "56mm",
    alignItems: "stretch",
    alignSelf: "center",
  },
  curvedBox: {
    flexDirection: "row",
    height: "100%",
    borderTopLeftRadius: "6mm",
    borderBottomLeftRadius: "6mm",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
    padding: "1mm",
    alignItems: "center",
  },
  singleImageCurvedBox: {
    justifyContent: "center",
  },
  animationImage: {
    width: "90mm",
    height: "100%",
    objectFit: "cover",
    resizeMode: "contain",
    borderRadius: "6mm",
  },
  imageGap: {
    width: "3mm",
  },
  placeholder: {
    width: "90mm",
    height: "50mm",
    backgroundColor: "#e0e0e0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "6mm",
  },
  placeholderText: {
    color: "#757575",
    fontSize: 12,
  },
  errorMessage: {
    color: "red",
    fontSize: 12,
    textAlign: "center",
    marginTop: 20,
  },
});
interface AnimationData {
  animationId: number;
  animationTagName: string;
  title: string;
  Introduction: string;
  incidencePrevalence: string;
  image1: string;
  causesRiskFactors: string;
  tagTitle: string;
  nameOfTagTitle: string;
  image2: string;
  symptoms: string;
  diagnosis: string;
  treatmentOptions: string;
  callToAction: string;
}

interface Props {
  animationData: AnimationData;
}

// Simplified HTMLText component that handles basic HTML tags

const HTMLText = ({
  children,
  style,
}: {
  children: string | null | undefined;
  style?: any;
}) => {
  if (!children) return null;

  // Default font size from style
  const fontSize = style?.fontSize || 12;

  // Split content into sections (handling <ul> separately)
  const sections = children.split(/(<ul>.*?<\/ul>)/s);

  return (
    <View>
      {sections.map((section, index) => {
        if (section.startsWith("<ul>") && section.endsWith("</ul>")) {
          // Extract list items
          const listItems = section.match(/<li>.*?<\/li>/gs) || [];
          return (
            <View key={`list-${index}`} style={{ marginBottom: 8 }}>
              {listItems.map((item, itemIndex) => {
                // Extract bold text inside list items
                const content = item
                  .replace(/<li>|<\/li>/g, "")
                  .split(/(<b>.*?<\/b>)/g)
                  .map((part, partIndex) => {
                    if (part.startsWith("<b>") && part.endsWith("</b>")) {
                      return (
                        <Text
                          key={`bold-${partIndex}`}
                          style={{ fontFamily: "Helvetica-Bold", fontSize }}
                        >
                          {part.replace(/<\/?b>/g, "")}
                        </Text>
                      );
                    }
                    return (
                      <Text key={`text-${partIndex}`} style={{ fontSize }}>
                        {part}
                      </Text>
                    );
                  });

                return (
                  <Text
                    key={`item-${itemIndex}`}
                    style={{ marginBottom: 4, fontSize }}
                  >
                    • {content}
                  </Text>
                );
              })}
            </View>
          );
        } else {
          // Handle normal text (bold and break handling)
          return (
            <Text key={`text-${index}`} style={{ ...style, fontSize }}>
              {section
                .split(/(<b>.*?<\/b>|<br\s*\/?>)/g)
                .map((part, partIndex) => {
                  if (part.startsWith("<b>") && part.endsWith("</b>")) {
                    return (
                      <Text
                        key={`bold-${partIndex}`}
                        style={{ fontFamily: "Helvetica-Bold", fontSize }}
                      >
                        {part.replace(/<\/?b>/g, "")}
                      </Text>
                    );
                  } else if (part.match(/<br\s*\/?>/)) {
                    return <Text key={`br-${partIndex}`}>{"\n"}</Text>; // Proper new line
                  }
                  return (
                    <Text key={`text-${partIndex}`} style={{ fontSize }}>
                      {part.replace(/<[^>]+>/g, "")}
                    </Text>
                  );
                })}
            </Text>
          );
        }
      })}
    </View>
  );
};

const PdfContent: React.FC<Props> = ({ animationData }) => {

  // Safety check for animationData
  if (!animationData || typeof animationData !== "object") {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={{ margin: 20 }}>
            <Text style={styles.errorMessage}>
              Error: Invalid animation data provided
            </Text>
          </View>
        </Page>
      </Document>
    );
  }

  const renderContent = () => {
    const content = [];
    let currentPage: any[] = [];
    let currentHeight = 0;
    const maxHeight = 247;

    const addToPage = (element: React.ReactNode, elementHeight: number) => {
      if (currentHeight + elementHeight > maxHeight && currentPage.length > 0) {
        content.push([...currentPage]);
        currentPage = [element];
        currentHeight = elementHeight;
      } else {
        currentPage.push(element);
        currentHeight += elementHeight;
      }
    };

    // Introduction
    if (animationData?.Introduction) {
      addToPage(
        <View style={styles.section}>
          <HTMLText style={styles.sectionTitle}>Introduction</HTMLText>
          <HTMLText style={styles.text}>{animationData.Introduction}</HTMLText>
        </View>,
        30
      );
    }

    // Image 1 with theme images

    if (animationData?.image1 || animationData?.image2) {
      addToPage(
        <View style={styles.imageContainer}>
          <View
            style={
              !animationData.image1 || !animationData.image2
                ? [
                    styles.curvedBox,
                    styles.singleImageCurvedBox,
                    { alignItems: "center", justifyContent: "center" },
                  ]
                : styles.curvedBox
            }
          >
            {/* Show Image 1 if available */}
            {animationData.image1 ? (
              <Image
                src={PDF_CUSTOM_UPLOAD_BASE_URL + animationData.image1}
                style={
                  !animationData.image2
                    ? [styles.animationImage, { alignSelf: "center" }] // Center if it's the only image
                    : styles.animationImage
                }
              />
            ) : animationData.image2 ? (
              // If image1 is missing but image2 exists, center image2
              <Image
                src={PDF_CUSTOM_UPLOAD_BASE_URL + animationData.image2}
                style={[styles.animationImage, { alignSelf: "center" }]}
              />
            ) : (
              // If no images are available, show a placeholder
              <View style={[styles.placeholder, { alignSelf: "center" }]}>
                <Text style={styles.placeholderText}>Image not available</Text>
              </View>
            )}

            {/* If both images are available, show image2 normally */}
            {animationData.image1 && animationData.image2 && (
              <>
                <View style={styles.imageGap} />
                <Image
                  src={PDF_CUSTOM_UPLOAD_BASE_URL + animationData.image2}
                  style={styles.animationImage}
                />
              </>
            )}
          </View>
        </View>,
        61
      );
    }

    // Incidence Prevalence
    if (animationData?.incidencePrevalence) {
      addToPage(
        <View style={styles.section}>
          <HTMLText style={styles.sectionTitle}>Incidence Prevalence</HTMLText>
          <HTMLText style={styles.text}>
            {animationData.incidencePrevalence}
          </HTMLText>
        </View>,
        30
      );
    }

    // Causes and Risk Factors
    if (animationData?.causesRiskFactors) {
      addToPage(
        <View style={styles.section}>
          <HTMLText style={styles.sectionTitle}>
            Causes and Risk Factors
          </HTMLText>
          <HTMLText style={styles.text}>
            {animationData.causesRiskFactors}
          </HTMLText>
        </View>,
        40
      );
    }

    // Types of Sleep Apnea
    if (
      animationData?.nameOfTagTitle ||
      animationData?.animationTagName ||
      animationData?.tagTitle
    ) {
      const titleText =
        [animationData?.nameOfTagTitle, animationData?.animationTagName]
          .filter(Boolean)
          .join(" ") || "Types";

      addToPage(
        <View style={styles.section}>
          <HTMLText style={styles.sectionTitle}>{titleText}</HTMLText>
          <HTMLText style={styles.text}>
            {animationData?.tagTitle || ""}
          </HTMLText>
        </View>,
        40
      );
    }

    // Symptoms & Diagnosis
    if (animationData?.symptoms || animationData?.diagnosis) {
      addToPage(
        <View style={styles.section}>
          {animationData?.symptoms && (
            <View style={{ marginBottom: 8 }}>
              <HTMLText style={styles.sectionTitle}>Symptoms</HTMLText>
              <HTMLText style={styles.text}>{animationData.symptoms}</HTMLText>
            </View>
          )}

          {animationData?.diagnosis && (
            <View>
              <HTMLText style={styles.sectionTitle}>Diagnosis</HTMLText>
              <HTMLText style={styles.text}>{animationData.diagnosis}</HTMLText>
            </View>
          )}
        </View>,
        40
      );
    }

    // Treatment Options
    if (animationData?.treatmentOptions) {
      addToPage(
        <View style={styles.section}>
          <HTMLText style={styles.sectionTitle}>Treatment Options</HTMLText>
          <HTMLText style={styles.text}>
            {animationData.treatmentOptions}
          </HTMLText>
        </View>,
        40
      );
    }

    // Call to Action with title
    if (animationData?.callToAction) {
      addToPage(
        <View style={styles.section}>
          <HTMLText style={styles.sectionTitle}>Call to Action</HTMLText>
          <HTMLText style={styles.text}>{animationData.callToAction}</HTMLText>
        </View>,
        25
      );
    }

    // Only add non-empty pages
    if (currentPage.length > 0) {
      content.push(currentPage);
    }

    // If no content was added, add a default message
    if (content.length === 0) {
      content.push([
        <View key="no-content" style={styles.section}>
          <Text style={styles.sectionTitle}>No Content Available in this</Text>
          <Text style={styles.text}>
            The animation data does not contain any content to display.
          </Text>
        </View>,
      ]);
    }

    return content;
  };

  const pages = renderContent().filter((page) => page.length > 0);

  return (
    <Document title={animationData.title}>
      {pages.map((pageContent, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page} wrap={false}>
          {backgroundImage && (
            <Image style={styles.backgroundImage} src={backgroundImage || ""} />
          )}

          <View style={styles.header}>
            <Text style={styles.headerText}>
              {animationData?.title || "Animation PDF"}
            </Text>
          </View>

          <View style={styles.content}>{pageContent}</View>
          <View style={styles.footer} fixed>
            <View style={styles.footerContent}>
              {/* Logo on the Left */}
              {logo ? (
                <Image style={{ width: "30mm" }} src={logo || ""} />
              ) : (
                <Text style={styles.footerText}>Clinical Visuals</Text>
              )}

              {/* Contact Details on the Right */}
              <View style={{ alignItems: "flex-end" }}>
                <Text style={styles.footerText}>
                  10xx, xxx, Country, zipcode{"\n"}
                  Phone: +1 123456789{"\n"}
                  Email: support@clinicalvisuals.com
                </Text>
              </View>
            </View>
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default PdfContent;
